/* Keep large media off the critical path, and request images at their display size. */
(function () {
  'use strict';

  function imageAtWidth(source, width) {
    if (!source || !/^(https?:|\/)/i.test(source)) return source;
    const url = new URL(source, location.href);
    const shopifyFile = url.hostname === 'cdn.shopify.com' && /^\/s\/files\//.test(url.pathname);
    const shopImage = url.origin === location.origin && /^\/cdn\/shop\/(?:files|articles|products|t\/\d+\/assets)\//.test(url.pathname);
    if ((!shopifyFile && !shopImage) || !/\.(jpe?g|png|webp|avif)$/i.test(url.pathname)) return source;
    url.searchParams.set('width', width);
    return url.href;
  }

  function responsive(img, widths, sizes) {
    if (!img || img.hasAttribute('srcset') || img.closest('picture')) return;
    const original = img.getAttribute('src');
    if (imageAtWidth(original, widths[0]) === original) return;
    img.setAttribute('sizes', sizes);
    img.setAttribute('srcset', widths.map(width => imageAtWidth(original, width) + ' ' + width + 'w').join(', '));
    img.setAttribute('src', imageAtWidth(original, widths.at(-1)));
    img.setAttribute('decoding', 'async');
  }

  const priorHome = home;
  home = function () {
    const template = document.createElement('template');
    template.innerHTML = priorHome();
    const video = template.content.querySelector('#harvest-film');
    const poster = template.content.querySelector('.hero-home .poster');
    if (poster) {
      poster.setAttribute('fetchpriority', 'high');
      responsive(poster, [640, 960, 1440], '100vw');
    }
    if (video) {
      const source = video.querySelector('source');
      if (source) {
        video.dataset.harvestSource = source.getAttribute('src');
        source.remove();
        video.removeAttribute('autoplay');
        video.removeAttribute('poster'); // The hero already has a separate poster image.
        video.preload = 'none';
        video.style.visibility = 'hidden';
      }
    }
    template.content.querySelectorAll('img:not(.poster)').forEach(img => {
      img.setAttribute('loading', 'lazy');
      responsive(img, [420, 720, 1080], '(max-width: 700px) 100vw, 50vw');
    });
    return template.innerHTML;
  };

  const priorBindFilm = bindFilm;
  const priorConnectPreviewVideo = connectPreviewVideo;
  connectPreviewVideo = function () {
    // The legacy preview hook also receives configured edition films. Preserve
    // the homepage's own responsive source when one has already been configured.
    if (document.querySelector('#harvest-film[data-harvest-source]') && !previewVideo.startsWith('blob:')) return;
    priorConnectPreviewVideo();
  };

  let harvestObserver;
  let harvestMotionObserver;
  bindFilm = function () {
    harvestObserver?.disconnect();
    harvestMotionObserver?.disconnect();
    const video = document.getElementById('harvest-film');
    if (!video?.dataset.harvestSource) return priorBindFilm();
    const button = document.getElementById('film-toggle');
    const poster = document.querySelector('.hero-home .poster');
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let inView = false;
    let manuallyPaused = false;

    function update() {
      const label = document.getElementById('film-label');
      const icon = document.getElementById('film-icon');
      if (label) label.textContent = video.paused ? 'Play film' : 'Pause film';
      if (icon) icon.textContent = video.paused ? '▶' : 'Ⅱ';
      button?.setAttribute('aria-label', video.paused ? 'Play harvest film' : 'Pause harvest film');
    }

    function attachSource() {
      if (video.hasAttribute('src')) return;
      const originals = window.THC.harvestVariants;
      const original = video.dataset.harvestSource;
      video.src = originals?.source === original
        ? (matchMedia('(max-width: 699px)').matches ? originals.mobile : originals.desktop)
        : original;
    }

    function autoplay() {
      if (!video.isConnected || !inView || manuallyPaused || motionOff() || reduceMotion.matches || navigator.connection?.saveData) return;
      attachSource();
      video.play().catch(update);
    }

    button?.addEventListener('click', () => {
      manuallyPaused = !video.paused;
      if (!manuallyPaused) attachSource();
    });
    video.addEventListener('play', update);
    video.addEventListener('playing', () => { video.style.visibility = ''; });
    video.addEventListener('pause', update);
    video.addEventListener('error', () => {
      video.style.display = 'none';
      const label = document.getElementById('film-label');
      if (label) label.textContent = 'Film unavailable';
      if (button) button.disabled = true;
    });
    update();
    const posterReady = poster?.decode().catch(() => {}) || Promise.resolve();
    harvestObserver = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      if (!inView) {
        video.pause();
        return;
      }
      posterReady.then(() => requestAnimationFrame(autoplay));
    }, { threshold: 0.2 });
    harvestObserver.observe(video.closest('.hero-home'));
    harvestMotionObserver = new MutationObserver(autoplay);
    harvestMotionObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-motion'] });
  };

  const priorProduct = product;
  product = function () {
    const template = document.createElement('template');
    template.innerHTML = priorProduct();
    template.content.querySelectorAll('.thc-shop-layout .media-stage img').forEach(img => {
      responsive(img, [450, 720, 1100], '(max-width: 699px) 90vw, (max-width: 1099px) 52vw, 34vw');
    });
    template.content.querySelectorAll('.thc-shop-layout .media-thumbnails img').forEach(img => {
      responsive(img, [80, 160], '52px');
    });
    template.content.querySelectorAll('.thc-product-origin img, .thc-product-flower img, .thc-recipe-poster img, .thc-social-gallery img').forEach(img => {
      responsive(img, [420, 680, 1000], '(max-width: 699px) 90vw, 48vw');
    });
    template.content.querySelectorAll('video[data-thc-origin-video]').forEach(video => {
      video.dataset.src = window.THC.originVideoVariants?.[video.dataset.src] || video.dataset.src;
      video.removeAttribute('poster');
    });
    return template.innerHTML;
  };

  const priorArticlePage = articlePage;
  articlePage = function (slug) {
    const template = document.createElement('template');
    template.innerHTML = priorArticlePage(slug);
    template.content.querySelectorAll('.origin-poster, .origin-field-photo img, .thc-recipe-poster img').forEach(img => {
      responsive(img, [640, 960, 1440], img.classList.contains('origin-poster') ? '100vw' : '(max-width: 699px) 92vw, 50vw');
    });
    // Keep the restored Wairarapa chapter films and their posters.
    template.content.querySelectorAll('video[data-film-src]').forEach(video => {
      if (video.closest('[data-origin-id="wairarapa"]')) return;
      video.dataset.filmSrc = window.THC.originVideoVariants?.[video.dataset.filmSrc] || video.dataset.filmSrc;
      video.removeAttribute('poster');
    });
    return template.innerHTML;
  };
})();
